import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useRouteMatch, useLocation, useHistory } from "react-router-dom";

import _ from "lodash";
import cx from "classnames";

import { generateReportUrl, parseQueryParams } from 'services/report.service';
import { DropdownButton, Form, Badge, ButtonGroup, Button } from "react-bootstrap";
import { padNumber } from 'services/util';
import dateformat from "dateformat";
import { FOURTEEN_DAYS } from 'constants/report.jsx';
import { getReportDatesForOption } from 'services/report.service';

import { fetchingWorkspaceReportSummary } from "actions/workspaceReports";
import WorkspaceStoryboardReports from "./storyboardReports";
import WorkspaceAssetAnalytics from "./assetAnalytics";
import WorkspaceLeadsReportsAnalytics from "./leadReports";
import { DatePickerView } from "../../reports/components/datePicker";
import TruncatedTextWithTooltip from "components/TruncatedTextWithTooltip";
import WorkspaceSessionAnalytics from "./sessionAnalytics";

import styles from '../workspaceReports.module.scss'

export default function WorkspaceReport(props) {
  const dispatch = useDispatch();
  const [activeSection, setActiveSection] = useState("reportSummary");

  useEffect(() => {
    dispatch(fetchingWorkspaceReportSummary());
  }, []);

  useEffect(() => {
    const sections = document.querySelectorAll("[id]");
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      {
        root: null,
        threshold: .9,
      }
    );

    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setActiveSection(id);
  };

  return(
    <>
      <div className={styles.container}>
        <div className={styles.navigationContainer}>
          <div className={styles.navigationMenu}>
            <button onClick={() => scrollToSection('reportSummary')}
              className={activeSection === "reportSummary" ? styles.activeMenuButton : ""}>Engagement Metrics
            </button>
            <button onClick={() => scrollToSection('storyboardReports')}
              className={activeSection === "storyboardReports" ? styles.activeMenuButton : ""}>Best Performing Storyboards
            </button>
            <button onClick={() => scrollToSection('leadAnalytics')}
              className={activeSection === "leadAnalytics" ? styles.activeMenuButton : ""}>Lead Analytics
            </button>
            <button onClick={() => scrollToSection('sessionAnalytics')}
              className={activeSection === "sessionAnalytics" ? styles.activeMenuButton : ""}>Session Analytics
            </button>
            <button onClick={() => scrollToSection('assetAnalytics')}
              className={activeSection === "assetAnalytics" ? styles.activeMenuButton : ""}>Asset Analytics
            </button>
          </div>
        </div>
        <div className={styles.reportsContainer} >
          <header className={styles.header} id='reportSummary'>
            <div className={styles.wrapper} >
              <h1>Workspace Reports</h1>
              <span>A Telescopic view of how your content is performing</span>
            </div>
          </header>
          <WorkspaceReportsSummary />
        </div>
      </div>
    </>
  )
}

export const WorkspaceReportsSummary = () => {
  const dispatch = useDispatch();
  let { url } = useRouteMatch();
  const { search } = useLocation();
  const history = useHistory();

  const [contentType, setContentType] = useState([]);
  const [types, setTypes] = useState([]);
  const [checkedItems, setCheckedItems] = useState([]);
  const [reportDate, setReportDate] = useState({
    type: FOURTEEN_DAYS,
    ...getReportDatesForOption(FOURTEEN_DAYS)
  });

  const summary = useSelector(state => _.get(state, `workspaces.reports.summary`, {}));

  const handleSelectedItemsChange = (selectedItems) => {
    setContentType(selectedItems);
    setCheckedItems(selectedItems);
  };

  const handleDateChange = (value) => {
    setReportDate(value);
  };

  const handleClearDate = () => {
    setReportDate({
      type: FOURTEEN_DAYS,
      ...getReportDatesForOption(FOURTEEN_DAYS)
    });
  };

  const handleRemoveType = (removedType) => {
    const updatedTypes = contentType.filter((type) => type !== removedType);
    setContentType(updatedTypes);
    setCheckedItems(updatedTypes);
  };

  useEffect(() => {
    const values = contentType.map(item => item.value);
    setTypes(values);
  }, [contentType]);

  useEffect(() => {
    if (reportDate) {
      const updatedUrl = generateReportUrl(url, reportDate, search);
      history.replace(updatedUrl);
    }
  }, [reportDate]);

  useEffect(() => {
    const parsedQuery = parseQueryParams(search);
    setReportDate(parsedQuery);
    
    if (parsedQuery.contentType) {
      setContentType(parsedQuery.contentType);
    }
  }, []);

  const _renderTimeSpent = (seconds) => {
    let minutes = padNumber(Math.floor(seconds / 60), 2);
    seconds = padNumber(Math.floor(seconds % 60), 2);
    var hours = padNumber(Math.floor(minutes / 60), 2);
    minutes = padNumber(minutes % 60, 2);

    hours = hours > 0 ? `${hours}` : '00';
    return `${hours}:${minutes}:${seconds}`;
  }

  return (
    <>
      <div className={styles.statisticsWrapper}>
        <div className={styles.statisticsView}>
          <WorkspaceVerticalTile heading="Sessions" subheading="Recorded" sectionId="" stat={summary.sessions || 0} path={'/images/reports/sessions.svg'} />
          <WorkspaceVerticalTile heading="Visitors" subheading="Attracted" stat={summary.visitors || 0} path={'/images/reports/visitors.svg'} />
          <WorkspaceVerticalTile heading="Leads" subheading="Captured" stat={summary.leads || 0} path={'/images/reports/leads.svg'} />
          <WorkspaceVerticalTile heading="Total Time" subheading="Spent" stat={_renderTimeSpent(summary.totalTimeSpent || 0)} path={'/images/reports/timespent.svg'} />
          <WorkspaceVerticalTile heading="Conversion" subheading="Rate" stat={`${_.get(summary, 'conversion', 0)}%`} path={'/images/reports/sessions.svg'} />
        </div>
      </div>
      
      <div className={styles.filterWrapper}>
        <DatePickerView value={reportDate} onChange={handleDateChange} />
        <ContentTypeSelector onChange={handleSelectedItemsChange} onClearDate={handleClearDate} checkedItems={checkedItems} setCheckedItems={setCheckedItems} />
      </div>

      <FilterBadges date={reportDate} type={contentType} onClearDate={handleClearDate} onRemoveType={handleRemoveType} />

      <WorkspaceStoryboardReports date={reportDate} contentType={types} sectionId="storyboardReports" />
      <WorkspaceLeadsReportsAnalytics date={reportDate} contentType={types} sectionId="leadAnalytics" />
      <WorkspaceSessionAnalytics date={reportDate} contentType={types} sectionId="sessionAnalytics" />
      <WorkspaceAssetAnalytics date={reportDate} contentType={types} sectionId="assetAnalytics" />
    </>
  )
}

const WorkspaceTimeSpent = ({ timespent }) => {

  const _renderTimeSpent = (seconds) => {
    let minutes = padNumber(Math.floor(seconds / 60), 2);
    seconds = padNumber(Math.floor(seconds % 60), 2);
    var hours = padNumber(Math.floor(minutes / 60), 2);
    minutes = padNumber(minutes % 60, 2);

    hours = hours > 0 ? `${hours}` : '00';
    return `${hours}:${minutes}:${seconds}`;
  }

  return (
    <div className={styles.timeSpentWrapper}>
      <div className={styles.content}>
        <div>
          <img src={'/images/reports/timespent.svg'} className={styles.icon} />
        </div>
        <div>
          <h5 className={styles.heading}>Total Time</h5>
          <p className={styles.subHeading}>Spent</p>
        </div>
      </div>
      <div className={styles.spentTime}>
        <p className={styles.timeFormat}>HH:MM:SS</p>
        <h4 className={styles.totalTime}>{_renderTimeSpent(timespent)}</h4>
      </div>
    </div>
  )
}

const WorkspaceGraphView = ({ converstion }) => {

  return (
    <div className={styles.graphViewWrapper}>
      <div className={styles.content}>
        <h5 className={styles.heading}>Conversion</h5>
        <p className={styles.subHeading}>Rate</p>
        <p className={styles.percentage}>{converstion}%</p>
      </div>

      <div className={styles.spentTime}>
        <div>
          <p className={styles.leads}>Leads</p>
        </div>
      </div>
    </div>
  )
}

const WorkspaceVerticalTile = ({ path, heading, subheading, stat, sectionId }) => {
  return (
    <div className={styles.wrapper} id={sectionId}>
      <div>
        <img src={path} className={styles.icon} />
      </div>
      <div>
        <h5 className={styles.heading}>{heading}</h5>
        <p className={styles.subHeading}>{subheading}</p>
        <p className={styles.stat}>{stat}</p>
      </div>
    </div>
  )
}

const ContentTypeSelector = ({ onChange, onClearDate, checkedItems, setCheckedItems }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const checkboxItems = [
    { label: "Interactive Content", value: "INTERACTIVE_CONTENT" },
    { label: "Landing Page", value: "LANDING_PAGE" },
    { label: "Resource Center", value: "RESOURCE_CENTER" },
    { label: "Spatial", value: "SPATIAL" }
  ];

  const handleCheckboxChange = (event, item) => {
    const isChecked = event.target.checked;
    if (isChecked) {
      setCheckedItems((prev) => [...prev, item]);
    } else {
      setCheckedItems((prev) => prev.filter((i) => i.value !== item.value));
    }
  };

  const applyCheckboxChanges = () => {
    setCheckedItems(checkedItems);
    setIsDropdownOpen(false);
    if (onChange) onChange(checkedItems);
  };

  const clearAllSelections = () => {
    setCheckedItems([]);
    if (onChange) onChange([]);
    if (onClearDate) onClearDate();
  };

  return (
    <div className={styles.filters}>
      <DropdownButton
        as={ButtonGroup}
        title="Content Type"
        className={styles.dropdown}
        show={isDropdownOpen}
        onToggle={(isOpen) => setIsDropdownOpen(isOpen)}
        data-track-event="Mixpanel" data-track-props={!isDropdownOpen ? "Content Type Filter" : null}
      >
        <Form>
          {checkboxItems.map((item, index) => (
            <Form.Check
              type="checkbox"
              label={item.label}
              key={index}
              id={`checkbox-${item.value}`}
              onChange={(e) => handleCheckboxChange(e, item)}
              checked={checkedItems.some((i) => i.value === item.value)}
            />
          ))}
        </Form>
        <div className={styles.applyButtonWrapper}>
          <Button className={styles.applyBtn} onClick={() => applyCheckboxChanges()}>
            Apply
          </Button>
        </div>
      </DropdownButton>
      {checkedItems && checkedItems.length > 0 &&
        <Button className={styles.clearAllButton} onClick={() => clearAllSelections()}>
          Clear All
        </Button>
      }
    </div>
  );
};

export const StoryboardDetails = ({ data }) => {
  const detail = data.storyboardResponse || "";
  const modifiedDate = data.lastPublished || "";
  const typeTextMap = {
    "INTERACTIVE_CONTENT": "Interactive Content",
    "LANDING_PAGE": "Landing Page",
    "RESOURCE_CENTER": "Resource Center",
    "SPATIAL": "Spatial",
  }

  return (
    <div className={styles.storyboradDetails}>
      <div className={styles.name}>
        <TruncatedTextWithTooltip content={detail.name} maxWidth={200} placement='top' />
      </div>
      <div className={styles.type}>
        {typeTextMap[detail.type]}
      </div>
      <div className={styles.date}>
        {dateformat(modifiedDate, "dd mmm yyyy")}
      </div>
    </div>
  )
}

export const AssetDetails = ({ data }) => {
  const name = _.get(data, 'asset.content.asset.name', '-');
  const imgUrl = _.get(data, 'asset.content.asset.icon.thumbnail', '');
  const fields = _.get(data, 'asset.fields', []);
  const sizeField = fields.find(field => field.name === "fileSize");
  const size = sizeField?.content || "";

  const typeField = fields.find(field => field.name === "contentType");
  const contentType = typeField?.content || "";

  return (
    <div className={styles.assetDetails}>
      <div className={styles.thumbnail}>
        <img className={styles.thumbnailImg} src={imgUrl} />
      </div>
      <div className={styles.details}>
        <TruncatedTextWithTooltip className={styles.name} content={name} maxWidth={200} placement='top' />
        <div className={styles.type}>{contentType} | {size}</div>
      </div>
    </div>
  )
}

const FilterBadges = ({type, onRemoveType}) => {
  return(
    <div className={styles.selectedItemsWrapper}>
      {type && type.map((value) => (
        <Badge className={styles.selectedItems} >
          {value.label} <span className={styles.badgeClose} onClick={() => onRemoveType(value)}>&times;</span>
        </Badge>
      ))}
    </div>
  )
}